import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Plane } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { track, EVENTS } from "@/lib/analytics";

const NAV = [
  { label: "Features", to: "/#features" },
  { label: "How it works", to: "/#how" },
  { label: "Guarantee", to: "/#guarantee" },
  { label: "Pricing", to: "/#pricing" },
  { label: "FAQ", to: "/#faq" },
];

export const Navbar = ({ topOffset = false }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goHash = (to) => {
    setOpen(false);
    const hash = to.split("#")[1];
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" }), 120);
    } else {
      document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const getStarted = () => {
    track(EVENTS.CTA_CLICK, { location: "navbar" });
    if (user) navigate(user.paid ? (user.onboarding_complete ? "/dashboard" : "/onboarding") : "/checkout");
    else navigate("/signup");
  };

  const onDark = location.pathname === "/" && !scrolled;

  return (
    <header
      className={`fixed ${topOffset ? "top-9" : "top-0"} inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-background/80 backdrop-blur-xl border-b border-border/60" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="navbar-logo">
            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-amber-400 to-red-600 flex items-center justify-center glow-cyan">
              <Plane className="h-4 w-4 text-white -rotate-45" />
            </div>
            <span className={`font-display text-lg font-bold tracking-tight ${onDark ? "text-white" : "text-foreground"}`}>sarkarijobai</span>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {NAV.map((n) => (
              <button
                key={n.label}
                onClick={() => goHash(n.to)}
                className={`text-sm transition-colors ${onDark ? "text-white/70 hover:text-white" : "text-muted-foreground hover:text-foreground"}`}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {n.label}
              </button>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <button
                onClick={() => navigate(user.paid ? (user.onboarding_complete ? "/dashboard" : "/onboarding") : "/checkout")}
                className={`text-sm font-medium transition-colors ${onDark ? "text-white hover:text-amber-300" : "text-foreground hover:text-primary"}`}
                data-testid="navbar-dashboard-link"
              >
                {user.paid ? "Dashboard" : "Complete setup"}
              </button>
            ) : (
              <Link to="/login" className={`text-sm font-medium transition-colors ${onDark ? "text-white/80 hover:text-white" : "text-muted-foreground hover:text-foreground"}`} data-testid="navbar-login">
                Log in
              </Link>
            )}
            <button
              onClick={getStarted}
              className={`rounded-full text-sm font-medium px-5 py-2.5 hover:scale-105 transition-transform ${onDark ? "bg-white text-black" : "bg-foreground text-background"}`}
              data-testid="navbar-get-started"
            >
              Get Started
            </button>
          </div>

          <button className={`md:hidden p-2 ${onDark ? "text-white" : "text-foreground"}`} onClick={() => setOpen(!open)} data-testid="navbar-menu-toggle">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-background border-b border-border px-4 py-4 space-y-3" data-testid="navbar-mobile-menu">
          {NAV.map((n) => (
            <button key={n.label} onClick={() => goHash(n.to)} className="block w-full text-left text-sm py-2 text-muted-foreground">
              {n.label}
            </button>
          ))}
          <div className="flex flex-col gap-2 pt-2">
            {!user && <Link to="/login" className="text-sm py-2" onClick={() => setOpen(false)}>Log in</Link>}
            <button onClick={getStarted} className="rounded-full bg-foreground text-background text-sm font-medium px-5 py-3">
              Get Started
            </button>
          </div>
        </div>
      )}
    </header>
  );
};
